import { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { ColorPicker, UnitPicker } from "../create";
import DashboardCreateSelectSensorModal from "../create/modal/SelectDevices";
import { COLORS } from "constant";
import { displaySensorType } from "utils/functions";
import { BlueText } from "components/common";
import { omit, set } from "lodash";
import { DeviceSelected } from "types";
import { itemQuantityHelper } from "../create/SensorCharts/CreateCardMultiple";
import { getWidgetConfigFromChartSource } from "../utils/widgetConfig";
import { GATEWAY_LABEL, LDSU_UUID_LABEL } from "constant/displayLabels";

const Wrapper = styled.div`
    background-color: #2f3a51;
    padding: 20px;
    border-radius: 10px;
    margin-bottom: 20px;
`;

type UpdateCardMultipleProps = {
    /** Panel data for update mode */
    panel: any;
    /** Device list (used to map attributes to device info) */
    devices: any[];
    /** Called when data changes */
    onChange: (data: { chartName: string; data: any[] }) => void;
};

// Helper to transform devices to attributes format
const toAttributes = (devices: { [key: string]: DeviceSelected }) => {
    return Object.values(devices).map((device) => ({
        ...device,
        device_id: device.ldsuUID,
        sensorType: device.sensorType,
    }));
};

/**
 * Update Card Multiple component
 */
const UpdateCardMultiple = ({
    panel,
    devices,
    onChange,
}: UpdateCardMultipleProps) => {
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState<{
        [key: string]: DeviceSelected;
    }>({});
    const [chartName, setChartName] = useState(panel?.name);
    const widgetConfig = useMemo(
        () => getWidgetConfigFromChartSource(panel.chart, panel.source),
        [panel.chart, panel.source]
    );

    // Initialize from panel data
    useEffect(() => {
        if (panel?.attributes && devices.length > 0) {
            const initialDevices: { [key: string]: DeviceSelected } = {};

            panel.attributes.forEach((attr: any, index: number) => {
                const matchedDevice = devices.find(
                    (d: any) =>
                        d.ldsuUID === attr.device_id &&
                        Number(d.said) === Number(attr.said)
                );

                if (matchedDevice) {
                    const deviceId = `${attr.device_id}/${attr.said}`;
                    initialDevices[deviceId] = {
                        ...matchedDevice,
                        deviceId,
                        color: attr.color || COLORS[index],
                        unit: attr.unit || matchedDevice.unit,
                    };
                }
            });

            setSelectedDevices(initialDevices);
            setChartName(panel.name || "Multi Sensor Values");
        }
    }, [panel, devices]);

    const handleChartNameChange = (name: string) => {
        setChartName(name);
        onChange({ chartName: name, data: toAttributes(selectedDevices) });
    };

    const onRemove = (device: DeviceSelected) => {
        const updated = omit(selectedDevices, device.deviceId);
        setSelectedDevices(updated);
        onChange({ chartName, data: toAttributes(updated) });
    };

    const handleDeviceChange = (
        device: DeviceSelected,
        changes: Partial<DeviceSelected>
    ) => {
        const device_ = { ...device, ...changes };
        const updated = set({ ...selectedDevices }, device.deviceId, device_);
        setSelectedDevices(updated);
        onChange({ chartName, data: toAttributes(updated) });
    };

    const handleSaveDevices = (data: any) => {
        const updated = Object.values(data)
            .map((item: any, index: number) => ({
                ...item,
                name: item.sensorName,
                color: selectedDevices[item.deviceId]?.color ?? COLORS[index],
                alert: false,
            }))
            .pluck("deviceId") as { [key: string]: DeviceSelected };

        setSelectedDevices(updated);
        onChange({ chartName, data: toAttributes(updated) });
    };

    return (
        <Wrapper>
            <Row>
                <Col sm="12">
                    <Form.Group>
                        <Form.Label>Widget name</Form.Label>
                        <Form.Control
                            name="name"
                            type="text"
                            value={chartName}
                            placeholder="Widget Name"
                            onChange={(event) =>
                                handleChartNameChange(event.target.value)
                            }
                        />
                    </Form.Group>

                    <div className="mt-2">
                        <Button
                            className="mr-2"
                            variant="primary"
                            onClick={() => setShowSelectModal(true)}
                        >
                            Select Sensors
                        </Button>
                        <BlueText>
                            {/* Set 1,1 as default so if we see 1,1 need to debug and fix the issue */}
                            (
                            {itemQuantityHelper(
                                widgetConfig ?? { minItems: 1, maxItems: 1 }
                            )}
                            )
                        </BlueText>
                    </div>
                    <DashboardCreateSelectSensorModal
                        show={showSelectModal}
                        allowSensorFilter={true}
                        onHide={() => setShowSelectModal(false)}
                        values={selectedDevices}
                        onSave={(event, data) => handleSaveDevices(data)}
                        deviceType="Sensor"
                        maxDevices={widgetConfig?.maxItems}
                    />
                </Col>
            </Row>
            <Row className="mt-4 cstm-table">
                <Col sm={12}>
                    {!!Object.values(selectedDevices).length && (
                        <div className="table-head">
                            <Row className="no-checkbox">
                                <Col md={2}>Sensor</Col>
                                <Col md={2}>Type</Col>
                                <Col xs={12} md={2}>
                                    {GATEWAY_LABEL}
                                </Col>
                                <Col md={2}>{LDSU_UUID_LABEL}</Col>
                                <Col md={1}>Unit</Col>
                                <Col md={2} className="text-center">
                                    Color
                                </Col>
                                <Col sm={1}></Col>
                            </Row>
                        </div>
                    )}
                    {Object.values(selectedDevices).map((device) => (
                        <div
                            key={device.deviceId}
                            id={device.deviceId}
                            className="table-row bg-secondary"
                        >
                            <Row className="no-checkbox">
                                <Col md={2} className="mb-md-0 mb-1">
                                    {device.sensorName}
                                </Col>
                                <Col md={2}>{displaySensorType(device)}</Col>
                                <Col
                                    xs={12}
                                    md={2}
                                    className="text-truncate mb-1 mb-md-0"
                                >
                                    <span title={device.gatewayName}>
                                        {device.gatewayName}
                                    </span>
                                </Col>
                                <Col md={2} className="text-truncate mb-md-1">
                                    <span title={device.ldsuUID}>
                                        {device.ldsuUID}
                                    </span>
                                </Col>
                                <Col
                                    xs={12}
                                    md={1}
                                    className="mb-md-0 mb-1 mb-md-0"
                                >
                                    <UnitPicker
                                        data={
                                            device.unitOptions || [device.unit]
                                        }
                                        defaultValue={device.unit}
                                        onChange={(_event: any, unit: string) =>
                                            handleDeviceChange(device, {
                                                unit: unit,
                                            })
                                        }
                                    />
                                </Col>
                                <Col
                                    xs={12}
                                    sm={2}
                                    className="d-md-flex justify-content-center mb-1 mb-md-0"
                                >
                                    <ColorPicker
                                        defaultValue={device.color as string}
                                        onChange={(event, color) =>
                                            handleDeviceChange(device, {
                                                color: color,
                                            })
                                        }
                                    />
                                </Col>
                                <Col
                                    xs={12}
                                    md={1}
                                    className="d-flex justify-content-end  mb-1 mb-md-0"
                                >
                                    <button
                                        type="button"
                                        className="btn p-0 border-0 bg-transparent"
                                        onClick={() => onRemove(device)}
                                        aria-label="Remove sensor"
                                    >
                                        <i className="material-icons cursor-pointer mr-2 text-primary-red-1">
                                            close
                                        </i>
                                    </button>
                                </Col>
                            </Row>
                        </div>
                    ))}
                </Col>
            </Row>
        </Wrapper>
    );
};

export { UpdateCardMultiple };
